import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import Button from "../../../../components/button/Button";
import RatingStars from "../../../../components/rating-stars/RatingStars";
import DropdownMenu from "../../../../components/dropdown-menu/DropdownMenu";
import MediaPreview from "../../../../components/media-preview/MediaPreview";
import DateDisplay, { DATE_DISPLAY_TYPE } from "../../../../components/date-display/DateDisplay";
import StatusBadge from "../../../../components/status-badge/StatusBadge";
import { SHOT_STATUS } from "../../../../constant/shotStatus";
import { getShotPlaybackSrc, useGetShotsQuery, useLazyGetShotsQuery, useUpdateShotStatusAnimatedMutation } from "../../../../app/services/shots";
import { useGetProjectQuery, useUpdateProjectMutation } from "../../../../app/services/projects";
import { useGetProjectMembershipQuery } from "../../../../app/accounts/memberships";
import { useGetCurrentUserQuery, useAddShotBookmarkMutation, useRemoveShotBookmarkMutation } from "../../../../app/accounts/authuser";
import FilterChipBar from "../../../../components/filter-chip/FilterChipBar";
import { TopAppBarSubRow } from "../../../../components/top-app-bar/TopAppBarHome";
import RatingModal from "../RatingModal";
import SvgLoadingAnimation from "../../../../components/loading";
import { isProducerGroupPosition, isClientGroupPosition } from "../../../../utils/memberPositionUtils";
import toast from "react-hot-toast";

const CompletedShotsListWrapper = styled(motion.div).attrs({
    className: "u-row u-gap16"
})`
    align-items: flex-start;
    flex-wrap: wrap;
    gap: ${props => props.$isMobile ? "8px" : "16px"};
    margin-bottom: 96px;
`;

const ButtonContainer = styled.div`
    position: absolute;
    right: ${props => props.position === 'right' ? '8px' : 'auto'};
    left: ${props => props.position === 'left' || props.position === 'top-left' ? '8px' : 'auto'};
    bottom: ${props => {
        switch (props.position) {
            case 'center':
                return '50%';
            case 'top-left':
                return 'auto';
            case 'bottom-quarter':
                return '25%';
            default:
                return '8px';
        }
    }};
    top: ${props => props.position === 'top-left' ? '8px' : 'auto'};
    left: 50%;
    transform: ${props => {
        switch (props.position) {
            case 'center':
                return 'translate(-50%, 50%)';
            case 'bottom-quarter':
                return 'translate(-50%, 50%)';
            default:
                return 'translateX(-50%)';
        }
    }};
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    .button-compact {
        gap: 4px !important;
    }
`;

const ShotContainer = styled.div.attrs({
    className: "u-relative u-row-center"
})`
    width: auto;
    height: auto;

    /* PC: ホバー中はオーバーレイ要素（検収ボタン、レーティング等）を非表示 */
    @media (min-width: 769px) {
        .shot-overlay {
            transition: opacity 0.25s ease;
        }
        &:hover .shot-overlay {
            opacity: 0;
            pointer-events: none;
        }
    }
`;

const BookmarkIcon = styled.span.attrs({
    className: "material-symbols-rounded"
})`
    position: absolute;
    top: -4px;
    right: 4px;
    color: #009ace;
    font-size: 32px;
    z-index: 1;
`;

const COMPLETED_STATUSES = [
    SHOT_STATUS.APPROVED,
    SHOT_STATUS.APPROVED_WITH_DELIVERY_FILE,
    SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED,
];

const APPROVED_STATUSES = [
    SHOT_STATUS.APPROVED,
    SHOT_STATUS.APPROVED_WITH_DELIVERY_FILE,
];

const COMPLETED_STATUS_FILTER = COMPLETED_STATUSES.join(",");

/** 完了ステータスかどうかを判定するヘルパー */
const isCompletedStatus = (status) => COMPLETED_STATUSES.includes(status);
const isApprovedStatus = (status) => APPROVED_STATUSES.includes(status);

const FADE_OUT_MS = 700;

/** タグの sort_rule を getShots API の sort_by / order に変換 */
const getSortParams = (sortRule) => {
    switch (sortRule) {
        case "name_asc":
            return { sort_by: "title", order: "asc" };
        case "newest":
        default:
            return { sort_by: "updated_at", order: "desc" };
    }
};

// 完了ショット（5,6,7）のみを表示するリスト
const CompletedShotsList = ({ onShotStatusChanged }) => {
    const { projectId } = useParams();
    const navigate = useNavigate();
    
    const { isMobile } = useSelector(state => state.device);
    const [fadingOutIds, setFadingOutIds] = useState(new Set());

    // ── タグフィルタ用 state ──
    const [selectedTagId, setSelectedTagId] = useState(null);

    // ── ページネーション用 state ──
    const [completedShots, setCompletedShots] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const sentinelRef = useRef(null);
    const isInitialLoadRef = useRef(true);
    const hasPlayedEntranceRef = useRef(false);
    
    const { 
        data: currentUserData, 
        isLoading: isLoadingUser 
    } = useGetCurrentUserQuery();

    // プロジェクト詳細（shot_tags 取得用）
    const {
        data: projectData,
    } = useGetProjectQuery(
        { project_id: projectId },
        { skip: !projectId || isLoadingUser || !currentUserData }
    );
    const shotTags = useMemo(
        () => projectData?.data?.project?.shot_tags || [],
        [projectData],
    );

    const { 
        data: membershipData, 
        isLoading: isLoadingMembership 
    } = useGetProjectMembershipQuery(projectId, {
        skip: !projectId || 
              isLoadingUser || 
              !currentUserData
    });

    const selectedTagSortParams = useMemo(() => {
        if (!selectedTagId) return null;
        const tag = shotTags.find((t) => t.id === selectedTagId);
        return tag?.sort_rule ? getSortParams(tag.sort_rule) : null;
    }, [selectedTagId, shotTags]);

    const shotsQueryArgs = useMemo(() => {
        const args = {
            project_id: projectId,
            status: COMPLETED_STATUS_FILTER,
        };
        if (selectedTagId) {
            args.tag = selectedTagId;
            if (selectedTagSortParams) {
                args.sort_by = selectedTagSortParams.sort_by;
                args.order = selectedTagSortParams.order;
            }
        }
        return args;
    }, [projectId, selectedTagId, selectedTagSortParams]);
    
    const { 
        data: shotsData, 
        isLoading, 
        isFetching,
        error 
    } = useGetShotsQuery(
        shotsQueryArgs,
        { 
            skip: !projectId || 
                  isLoadingUser || 
                  isLoadingMembership || 
                  !currentUserData || 
                  !membershipData 
        }
    );

    // タグ切り替え時にページネーションをリセット
    const handleTagSelect = useCallback((tagId) => {
        setSelectedTagId(tagId);
        setCompletedShots([]);
        setHasMore(false);
        setCurrentOffset(0);
        isInitialLoadRef.current = true;
        hasPlayedEntranceRef.current = false;
    }, []);

    const [updateProject] = useUpdateProjectMutation();

    const handleTagReorder = useCallback((reorderedTags) => {
        if (!projectId) return;
        const payload = reorderedTags.map((t) => ({
            id: t.id,
            name: t.name,
            sort_rule: t.sort_rule,
        }));
        updateProject({ project_id: projectId, shot_tags: payload });
    }, [projectId, updateProject]);

    const [fetchMoreShots] = useLazyGetShotsQuery();
    
    const [updateShotStatus] = useUpdateShotStatusAnimatedMutation();
    const [addShotBookmark] = useAddShotBookmarkMutation();
    const [removeShotBookmark] = useRemoveShotBookmarkMutation();
    const memberPosition = membershipData?.data?.position;
    const isReadOnly = membershipData?.data?.is_read_only ?? true;
    const canRate = membershipData?.data?.can_rate ?? false;
    const isClientGroup = memberPosition ? isClientGroupPosition(memberPosition) : false;
    const isProducerOrDirector = memberPosition ? isProducerGroupPosition(memberPosition) : false;
    const bookmarkedShotIds = useMemo(
        () => new Set(currentUserData?.data?.bookmarked_shot_ids || []),
        [currentUserData?.data?.bookmarked_shot_ids]
    );

    const [ratingModalShot, setRatingModalShot] = useState(null);

    /** フェードアウト → API → ローカル更新 の共通フロー */
    const animatedStatusChange = useCallback(async (shot, newStatus, { removeFromList = false } = {}) => {
        setFadingOutIds(prev => new Set(prev).add(shot.id));

        try {
            await Promise.all([
                updateShotStatus({ project_id: projectId, shot_id: shot.id, status: newStatus }).unwrap(),
                new Promise(r => setTimeout(r, FADE_OUT_MS)),
            ]);

            if (removeFromList) {
                setCompletedShots(prev => prev.filter(s => s.id !== shot.id));
            } else {
                setCompletedShots(prev =>
                    prev.map(s => (s.id === shot.id ? { ...s, status: newStatus } : s))
                );
                setFadingOutIds(prev => {
                    const next = new Set(prev);
                    next.delete(shot.id);
                    return next;
                });
            }
            onShotStatusChanged?.();
        } catch (error) {
            setFadingOutIds(prev => {
                const next = new Set(prev);
                next.delete(shot.id);
                return next;
            });
            toast.error("ステータスの更新に失敗しました");
            console.error("Failed to update shot status:", error);
        }
    }, [projectId, updateShotStatus, onShotStatusChanged]);

    const handleApproval = useCallback((shot, approve) => {
        const newStatus = approve
            ? SHOT_STATUS.APPROVED
            : isClientGroup
                ? SHOT_STATUS.CLIENT_PENDING
                : SHOT_STATUS.PRODUCER_PENDING;
        const leavesCompletedList = !isCompletedStatus(newStatus);
        animatedStatusChange(shot, newStatus, { removeFromList: leavesCompletedList });
    }, [isClientGroup, animatedStatusChange]);

    const handleSetHold = useCallback((shot) => {
        animatedStatusChange(shot, SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED);
    }, [animatedStatusChange]);

    const handleCancelHold = useCallback((shot) => {
        const revertStatus = isClientGroup
            ? SHOT_STATUS.CLIENT_PENDING
            : SHOT_STATUS.PRODUCER_PENDING;
        animatedStatusChange(shot, revertStatus, { removeFromList: true });
    }, [isClientGroup, animatedStatusChange]);

    // ── 初回: completedShots を初期化 / 再フェッチ時: ページネーション済みデータを保持しつつ1ページ目をマージ ──
    useEffect(() => {
        if (!shotsData?.data) return;

        const freshShots = shotsData.data.shots || [];

        if (isInitialLoadRef.current) {
            setCompletedShots(freshShots);
            setHasMore(shotsData.data.has_next);
            setCurrentOffset(shotsData.data.next_offset ?? 0);
            isInitialLoadRef.current = false;
        } else {
            setCompletedShots(prev => {
                const freshIdSet = new Set(freshShots.map(s => s.id));
                const paginatedShots = prev.filter(s => !freshIdSet.has(s.id));
                return [...freshShots, ...paginatedShots];
            });
        }
    }, [shotsData]);

    // ── 追加ページ読み込み ──
    const loadMoreShots = useCallback(async () => {
        if (!projectId || !hasMore || isLoadingMore) return;

        setIsLoadingMore(true);
        try {
            const fetchArgs = {
                project_id: projectId,
                status: COMPLETED_STATUS_FILTER,
                offset: currentOffset,
            };
            if (selectedTagId) {
                fetchArgs.tag = selectedTagId;
                if (selectedTagSortParams) {
                    fetchArgs.sort_by = selectedTagSortParams.sort_by;
                    fetchArgs.order = selectedTagSortParams.order;
                }
            }
            const result = await fetchMoreShots(fetchArgs).unwrap();

            if (result?.data?.shots && result.data.shots.length > 0) {
                setCompletedShots(prev => {
                    const existingIds = new Set(prev.map(s => s.id));
                    const unique = result.data.shots.filter(s => !existingIds.has(s.id));
                    return [...prev, ...unique];
                });
                setHasMore(result.data.has_next);
                setCurrentOffset(result.data.next_offset ?? 0);
            } else {
                setHasMore(false);
            }
        } catch (err) {
            console.error("[loadMoreShots] エラー:", err);
        } finally {
            setIsLoadingMore(false);
        }
    }, [projectId, hasMore, isLoadingMore, currentOffset, fetchMoreShots, selectedTagId, selectedTagSortParams]);

    // ── IntersectionObserver でリスト末尾を監視 ──
    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
                    loadMoreShots();
                }
            },
            { rootMargin: "200px" }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, isLoadingMore, loadMoreShots]);

    const shouldAnimateEntrance = !hasPlayedEntranceRef.current;

    const containerVariants = {
        hidden: {},
        show: {
            transition: {
                staggerChildren: shouldAnimateEntrance ? 0.1 : 0,
            }
        }
    };

    const itemVariants = shouldAnimateEntrance
        ? {
            hidden: { opacity: 0, y: 20 },
            show: {
                opacity: 1,
                y: 0,
                transition: { duration: 1.0 }
            }
        }
        : {
            hidden: { opacity: 1, y: 0 },
            show: { opacity: 1, y: 0 },
        };

    const handleAddBookmark = useCallback(async (shotId) => {
        try {
            await addShotBookmark(shotId).unwrap();
        } catch {
            toast.error("ブックマークの追加に失敗しました");
        }
    }, [addShotBookmark]);

    const handleRemoveBookmark = useCallback(async (shotId) => {
        try {
            await removeShotBookmark(shotId).unwrap();
        } catch {
            toast.error("ブックマークの解除に失敗しました");
        }
    }, [removeShotBookmark]);

    const getMenuItems = (shot) => {
        const items = [];

        if (isClientGroup) {
            const canApproveShot = !isReadOnly;
            if (shot.status === SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED) {
                items.push({
                    label: "保留を取り消す",
                    color: "",
                    icon: "undo",
                    onClick: () => handleCancelHold(shot)
                });
            } else if (!isApprovedStatus(shot.status)) {
                items.push({
                    label: "検収OKにする",
                    color: "",
                    icon: "check_circle",
                    onClick: () => canApproveShot ? handleApproval(shot, true) : toast.error("閲覧のみのため検収操作できません")
                });
            } else {
                items.push({
                    label: "検収OKを取り消す",
                    color: "",
                    icon: "cancel",
                    onClick: () => handleApproval(shot, false)
                });
            }
        } else {
            if (shot.status === SHOT_STATUS.PRODUCER_PENDING) {
                items.push({
                    label: "提出予定日を設定",
                    color: "",
                    icon: "calendar_month",
                    onClick: () => alert("提出予定日を設定")
                });
            }
            
            if (shot.status === SHOT_STATUS.CLIENT_PENDING) {
                items.push({
                    label: "自分のタスクにする",
                    color: "",
                    icon: "assignment_ind",
                    onClick: () => alert("自分のタスクにする")
                });
            }

            if (shot.status === SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED) {
                items.push({
                    label: "保留を取り消す",
                    color: "",
                    icon: "undo",
                    onClick: () => handleCancelHold(shot)
                });
            }

            if (isApprovedStatus(shot.status)) {
                items.push({
                    label: "このショットを保留",
                    color: "",
                    icon: "do_not_disturb_on",
                    onClick: () => handleSetHold(shot)
                });
                items.push({
                    label: "検収OKを取り消す",
                    color: "",
                    icon: "cancel",
                    onClick: () => handleApproval(shot, false)
                });
            }

            items.push({
                label: "削除",
                color: "",
                icon: "delete",
                onClick: () => alert("削除")
            });
        }

        if (canRate) {
            const hasRated = shot.my_rating != null && shot.my_rating > 0;
            items.push({
                label: hasRated ? "評価を更新" : "評価する",
                color: "",
                icon: "star",
                onClick: () => setRatingModalShot(shot)
            });
        }

        if (bookmarkedShotIds.has(shot.id)) {
            items.push({
                label: "ブックマーク解除",
                color: "",
                icon: "bookmark_remove",
                onClick: () => handleRemoveBookmark(shot.id)
            });
        } else {
            items.push({
                label: "ブックマーク追加",
                color: "",
                icon: "bookmark_add",
                onClick: () => handleAddBookmark(shot.id)
            });
        }

        return items;
    };

    const handlePreviewClick = (shot) => {
        if (projectId && shot?.id) {
            // ⌥[ / ⌥] で完了ショット間を移動するための並び（読み込み済みの
            // タグ絞り込み後の表示順。Preview 側は usePreviewQueueShortcut が読む）
            const shotIds = completedShots.map((s) => s.id);
            navigate(`/${projectId}/${shot.id}`, {
                state: {
                    shot,
                    ...(shotIds.length > 0
                        ? {
                              previewQueue: {
                                  source: "completed",
                                  shotIds,
                              },
                          }
                        : {}),
                },
            });
        }
    };

    const renderThumbnailOverlay = (shot) => {
        if (isClientGroup) {
            const ratingDisplay = (
                <RatingStars
                    rating={shot.my_rating}
                    isClickable={canRate}
                    onClick={canRate ? () => setRatingModalShot(shot) : undefined}
                />
            );

            if (shot.status === SHOT_STATUS.APPROVED || shot.status === SHOT_STATUS.PRODUCER_PENDING) {
                return ratingDisplay;
            }

            const buttonText = shot.status === SHOT_STATUS.NEW
                ? "確認"
                : `テイク${shot.variation_order || 0}を確認`;

            return (
                <>
                    <ButtonContainer $position="right">
                        <Button
                            theme="tertiary"
                            variant="text"
                            size="small"
                            rightIcon="chevron_right"
                            onClick={() => handlePreviewClick(shot)}
                            className="button-compact"
                        >
                            {buttonText}
                        </Button>
                    </ButtonContainer>
                    {ratingDisplay}
                </>
            );
        } else {
            const ratingCount = shot.ratings ? shot.ratings.length : 0;
            const ratingDisplay = (
                <RatingStars
                    rating={shot.average_rating}
                    isClickable={false}
                    showRatingText={isProducerOrDirector && ratingCount > 0}
                    ratingCount={ratingCount}
                />
            );

            if (shot.status === SHOT_STATUS.PRODUCER_PENDING) {
                return (
                    <>
                        <ButtonContainer position="bottom-quarter">
                            <Button
                                theme="tertiary"
                                variant="filled"
                                size="small"
                                width="fit"
                                leftIcon="reply"
                                onClick={() => handlePreviewClick(shot)}
                                className="button-compact"
                            >
                                返信
                            </Button>
                        </ButtonContainer>
                        <ButtonContainer position="center">
                            <Button
                                theme="secondary"
                                variant="filled"
                                size="small"
                                rightIcon="chevron_right"
                                onClick={() => alert('提出予定')}
                                className="button-compact"
                            >
                                {`テイク${(shot.variation_order || 0) + 1}を提出`}
                            </Button>
                        </ButtonContainer>
                        {ratingDisplay}
                    </>
                );
            }

            return ratingDisplay;
        }
    };

    const shouldAutoPlay = (shot) => {
        if (isClientGroup) {
            return ![
                SHOT_STATUS.PRODUCER_PENDING,
                SHOT_STATUS.APPROVED,
                SHOT_STATUS.APPROVED_WITH_DELIVERY_FILE,
                SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED,
            ].includes(shot.status);
        }
        return shot.status === SHOT_STATUS.PRODUCER_PENDING;
    };

    useEffect(() => {
        if (completedShots.length > 0) {
            hasPlayedEntranceRef.current = true;
        }
    }, [completedShots.length]);

    if (isLoading) {
        return (
            <div className="u-col-center u-w100 u-mt48">
                <SvgLoadingAnimation />
            </div>
        );
    }

    if (error) {
        return (
            <div className="u-col-center u-w100 u-mt48">
                <p className="u-text-green">ショットデータの取得に失敗しました</p>
            </div>
        );
    }

    const tagFilterRow = (
        <TopAppBarSubRow insideScrollContainer>
            <FilterChipBar
                items={shotTags}
                selectedId={selectedTagId}
                onSelect={handleTagSelect}
                onReorder={handleTagReorder}
            />
        </TopAppBarSubRow>
    );

    if (!completedShots || completedShots.length === 0) {
        return (
            <>
                {tagFilterRow}
                {isFetching ? (
                    <div className="u-col-center u-w100 u-mt48">
                        <SvgLoadingAnimation />
                    </div>
                ) : (
                    <div className="u-col-center u-w100 u-mt48">
                        <p className="u-text-light-gray">ショットがありません</p>
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            {tagFilterRow}
            <CompletedShotsListWrapper
                $isMobile={isMobile}
                variants={containerVariants}
                initial={shouldAnimateEntrance ? "hidden" : false}
                animate="show"
            >
                <AnimatePresence mode="popLayout">
                    {completedShots.map((shot, index) => {
                        const isBookmarked = bookmarkedShotIds.has(shot.id);
                        const isFading = fadingOutIds.has(shot.id);

                        return (
                            <motion.div
                                key={shot.id || index}
                                variants={itemVariants}
                                exit={{ opacity: 0, transition: { duration: 0 } }}
                                className={isFading ? "fade-out-bck" : undefined}
                                style={isFading ? { pointerEvents: "none" } : undefined}
                            >
                                <ShotContainer>
                                    <div style={{ position: 'relative', cursor: 'pointer' }} onClick={() => handlePreviewClick(shot)}>
                                        <MediaPreview
                                            src={getShotPlaybackSrc(shot) || shot.thumbnail}
                                            thumbnailSrc={shot.thumbnail || undefined}
                                            sizeMode={{ mode: 'fixed-height', height: isMobile ? '128px' : '192px' }}
                                            autoPlay={shouldAutoPlay(shot)}
                                            hoverPlay={!isMobile}
                                            disableHoverEffect={true}
                                        />
                                        <div className="shot-overlay">
                                            {renderThumbnailOverlay(shot)}
                                        </div>
                                        {shot.status === SHOT_STATUS.PRODUCER_DONE_CLIENT_UNAPPROVED && (
                                            <StatusBadge variant="overlay" label="保留" />
                                        )}
                                    </div>
                                    {isBookmarked && (
                                        <BookmarkIcon>
                                            bookmark
                                        </BookmarkIcon>
                                    )}
                                </ShotContainer>
                                <div className="u-row-between u-w100">
                                    <p>{shot.shot_name}</p>
                                    <DropdownMenu items={getMenuItems(shot)} />
                                </div>
                                <div className="u-text-light-gray bodytext-11 u-line-height-150">
                                    <div>
                                        <DateDisplay date={shot.updated_at} type={DATE_DISPLAY_TYPE.SHORT_DATE_TIME} />
                                    </div>
                                    {/* 完了リストでは実質到達しない想定の保険条件（未検収かつ納期あり） */}
                                    {!isApprovedStatus(shot.status) && shot.next_delivery_date && (
                                        <div>
                                            <DateDisplay date={shot.next_delivery_date} type={DATE_DISPLAY_TYPE.FULL_DATE} />
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </CompletedShotsListWrapper>

            {/* 無限スクロール用センチネル & ローディング */}
            <div ref={sentinelRef} style={{ width: '100%', height: 1 }} />
            {isLoadingMore && (
                <div className="u-col-center u-w100 u-mt16 u-mb16">
                    <SvgLoadingAnimation />
                </div>
            )}

            {ratingModalShot && (
                <RatingModal
                    shot={ratingModalShot}
                    onClose={() => setRatingModalShot(null)}
                />
            )}
        </>
    );
}

export default CompletedShotsList;
